import React from 'react';
import PropTypes from 'prop-types';
import styles from './MobileGameRow.module.css';
import { formatGameDateTime } from '../../utils/timeUtils';
import { formatLine } from '../../utils/lineDataUtils';

const MobileGameRow = ({
  game,
  displayedWeek,
  locksCache,
  onGameTap,
  className,
  shouldHideLockIndicators = false,
  isUserLock = false,
  userLock = null
}) => {


  const { displayDate, displayTime } = formatGameDateTime(game.start_date);
  const gameLocks = locksCache[displayedWeek || 0]?.filter(lock => lock.gameId === game.id) || [];

  const hasSpread = game.betting?.spread != null;
  const hasOverUnder = game.betting?.over_under != null;

  const getLockIndicators = () => {
    const indicators = [];
    if (gameLocks.some(lock => lock.pickType === 'away')) {
      const awayLock = gameLocks.find(lock => lock.pickType === 'away');
      indicators.push({
        type: 'away',
        text: `${game.away_team.abbreviation || game.away_team.name} ${formatLine(awayLock.lockLine)}`
      });
    }
    if (gameLocks.some(lock => lock.pickType === 'home')) {
      const homeLock = gameLocks.find(lock => lock.pickType === 'home');
      indicators.push({
        type: 'home',
        text: `${game.home_team.abbreviation || game.home_team.name} ${formatLine(homeLock.lockLine)}`
      });
    }
    if (gameLocks.some(lock => lock.pickType === 'over')) {
      const overLock = gameLocks.find(lock => lock.pickType === 'over');
      indicators.push({
        type: 'over',
        text: `O ${overLock.lockLine}`
      });
    }
    if (gameLocks.some(lock => lock.pickType === 'under')) {
      const underLock = gameLocks.find(lock => lock.pickType === 'under');
      indicators.push({
        type: 'under',
        text: `U ${underLock.lockLine}`
      });
    }
    return indicators;
  };

  return (
    <button
      type="button"
      className={`${styles.gameRow} ${isUserLock ? styles.userLock : ''} ${className || ''}`}
      onClick={() => onGameTap(game)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onGameTap(game);
        }
      }}
      data-game-id={game.id}
      aria-label={`Game: ${game.away_team.name} at ${game.home_team.name} on ${displayDate} at ${displayTime}`}
    >
      <div className={styles.gameInfo}>
        <div className={styles.matchup}>
          <span className={styles.team}>
            {game.away_team.rank && <span className={styles.rank}>#{game.away_team.rank}</span>}
            {game.away_team.name}
          </span>
          <span className={styles.vs}> @ </span>
          <span className={styles.team}>
            {game.home_team.name}
            {game.home_team.rank && <span className={styles.rank}>#{game.home_team.rank}</span>}
          </span>
        </div>
        <div className={styles.dateTime}>
          {displayDate} • {displayTime}
          {game.game_status === 'in_progress' && (
            <span className={styles.statusBadge} style={{ backgroundColor: '#ff6b35', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', marginLeft: '8px' }}>
              LIVE
            </span>
          )}
          {game.game_status === 'completed' && (
            <span className={styles.statusBadge} style={{ backgroundColor: '#6f42c1', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', marginLeft: '8px' }}>
              FINAL
            </span>
          )}
          {game.game_status === 'scheduled' && new Date(game.start_date) > new Date() && (
            <span className={styles.statusBadge} style={{ backgroundColor: '#28a745', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', marginLeft: '8px' }}>
              UPCOMING
            </span>
          )}
        </div>
        {(game.game_status === 'in_progress' || game.game_status === 'completed') && (
          <div className={styles.score} style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px', color: '#fff' }}>
            {game.away_team.abbreviation || game.away_team.name} {game.away_score || 0} - {game.home_score || 0} {game.home_team.abbreviation || game.home_team.name}
          </div>
        )}
      </div>

      <div className={styles.bettingInfo}>
        <div className={styles.spread}>
          Spread: {hasSpread ? `${game.home_team.abbreviation || game.home_team.name} ${formatLine(game.betting.spread)}` : 'N/A'}
        </div>
        <div className={styles.total}>
          Total: {hasOverUnder ? game.betting.over_under : 'N/A'}
        </div>
      </div>

      {/* User Lock Indicator */}
      {isUserLock && userLock && (
        <div className={styles.userLockIndicator}>
          <span className={styles.userLockLabel}>Your Pick:</span>
          <span className={styles.userLockPick}>
            {userLock.pickType === 'home' ? `${game.home_team.name} ${formatLine(userLock.lockLine)}` :
             userLock.pickType === 'away' ? `${game.away_team.name} ${formatLine(userLock.lockLine)}` :
             `${userLock.pickType.charAt(0).toUpperCase() + userLock.pickType.slice(1)} ${formatLine(userLock.lockLine)}`}
          </span>
        </div>
      )}

      {gameLocks.length > 0 && !shouldHideLockIndicators && (
        <div className={styles.lockIndicators}>
          {getLockIndicators().map(indicator => (
            <span key={indicator.type} className={`${styles.indicator} ${styles[indicator.type]}`}>
              {indicator.text}
            </span>
          ))}
        </div>
      )}
    </button>
  );
};

MobileGameRow.propTypes = {
  game: PropTypes.object.isRequired,
  displayedWeek: PropTypes.number,
  locksCache: PropTypes.object.isRequired,
  onGameTap: PropTypes.func.isRequired,
  className: PropTypes.string,
  shouldHideLockIndicators: PropTypes.bool,
  isUserLock: PropTypes.bool,
  userLock: PropTypes.object
};

export default MobileGameRow;
